import type { Workspace } from "@/lib/api";
import {
  formatThaiDatetimeFromDate,
  parseDatetimeInput,
} from "@aera/data-table";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { createMarketApiFetch } from "@/lib/marketApiFetch";

const marketApiFetch = createMarketApiFetch();

export type OrdersPendingResponse = {
  status?: string;
  user_id?: string;
  announcements?: unknown[];
  slips?: unknown[];
  counts?: HotQueueKindCounts;
};

export type OrdersReviewResponse = {
  status?: string;
  user_id?: string;
  announcements?: unknown[];
  addresses?: unknown[];
  slips?: unknown[];
  counts?: HotQueueKindCounts;
};

export type HotQueueKindCounts = {
  announcements?: number;
  slips?: number;
  addresses?: number;
};

/** UI totals after JSON — every kind is a number so setState type-checks. */
export type HotQueueKindCountTotals = {
  announcements: number;
  slips: number;
  addresses: number;
};

export type ReceivableListQuery = {
  kind?: ReceivableKind;
  dateFrom?: string | null;
  dateTo?: string | null;
  q?: string | null;
  status?: string | null;
};

export type InactiveOrderReason = "dismissed" | "expired";

export type InactiveOrderItem = {
  kind: "slip" | "announcement" | "address" | string;
  entity_id: string;
  order_id?: string;
  queue_left_at?: string | null;
  queue_left_reason?: InactiveOrderReason | string | null;
  workspace_id?: string;
  title?: string;
  amount?: number;
  amount_remaining?: number;
  amount_allocated?: number;
  amount_paid?: number;
  status?: string;
  transfer_date?: string;
  sender_bank?: string;
  source_ref?: string;
  addr_phone?: string;
  address_text?: string;
  created_at?: string | null;
};

export type OrdersInactiveResponse = {
  status?: string;
  user_id?: string;
  count?: number;
  items?: InactiveOrderItem[];
};

export type ReceivableDebtsSummary = {
  pendingCount: number;
  reviewCount: number;
  totalCount: number;
  cardSubtitle: string;
  detailNowLabel: string;
};

export type ReceivableBucket = "pending" | "review" | "dismissed" | "expired";

export type ReceivableKind = "announcement" | "address" | "slip";

export function isReceivableAnnouncementItem(
  item: ReceivableListItem,
): boolean {
  return (item.kindLabel ?? "").includes("ประกาศ");
}

export function isReceivableSlipItem(item: ReceivableListItem): boolean {
  return (item.kindLabel ?? "").includes("สลิป");
}

export function isReceivableAddressItem(item: ReceivableListItem): boolean {
  return (item.kindLabel ?? "").includes("ที่อยู่");
}

export function filterReceivableItemsByKind(
  items: ReceivableListItem[],
  kind: ReceivableKind,
): ReceivableListItem[] {
  const predicate = {
    announcement: isReceivableAnnouncementItem,
    slip: isReceivableSlipItem,
    address: isReceivableAddressItem,
  }[kind];
  return items.filter(predicate);
}

/** Keep this shop's items. Untagged legacy Redis rows stay visible; other shops drop. */
export function filterReceivableItemsByWorkspace(
  items: ReceivableListItem[],
  workspaceId: string,
): ReceivableListItem[] {
  const ws = workspaceId.trim();
  if (!ws) return items;
  return items.filter((item) => {
    const itemWs = (item.workspaceId || "").trim();
    return !itemWs || itemWs === ws;
  });
}

export type ReceivableListsBundle = {
  items: ReceivableListItem[];
  pending: ReceivableListItem[];
  review: ReceivableListItem[];
  byKind: Record<ReceivableKind, ReceivableListItem[]>;
};

export function filterReceivableListsByWorkspace(
  lists: ReceivableListsBundle,
  workspaceId: string,
): ReceivableListsBundle {
  const ws = workspaceId.trim();
  if (!ws) return lists;
  const byKind: Record<ReceivableKind, ReceivableListItem[]> = {
    announcement: filterReceivableItemsByWorkspace(lists.byKind.announcement, ws),
    address: filterReceivableItemsByWorkspace(lists.byKind.address, ws),
    slip: filterReceivableItemsByWorkspace(lists.byKind.slip, ws),
  };
  const items = [...byKind.announcement, ...byKind.address, ...byKind.slip];
  const pending = filterReceivableItemsByWorkspace(lists.pending, ws);
  const review = filterReceivableItemsByWorkspace(lists.review, ws);
  return { items, pending, review, byKind };
}

export type ReceivableField = {
  key: string;
  label: string;
  value: string;
};

export type ReceivableTableColumn = {
  key: string;
  label: string;
};

export type ReceivableListItem = {
  key: string;
  mergeKey: string;
  statuses: ReceivableBucket[];
  title: string;
  detail: string | null;
  amountLabel: string | null;
  kindLabel: string | null;
  createdAt: string | null;
  transferDate: string | null;
  addrPhone: string | null;
  fields: ReceivableField[];
  /** Hidden upstream ids for history/correction APIs (not shown in table fields). */
  receivableId?: string | null;
  paymentId?: string | null;
  addressId?: string | null;
  /** Canonical shop id — used to keep สลิปค้าง / ยอดค้าง workspace-scoped. */
  workspaceId?: string | null;
};

export function buildReceivableSlipImageUrl(
  imageId: unknown,
  ownerUserId: string | null | undefined,
): string | null {
  const id = pickNonEmptyString(
    typeof imageId === "number" ? String(imageId) : imageId,
  );
  const owner = ownerUserId?.trim();
  if (!id || !owner) return null;
  const params = new URLSearchParams({ owner_user_id: owner });
  return `/api/v1/orders/images/${encodeURIComponent(id)}?${params.toString()}`;
}

function isResolvableSlipImageUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (trimmed.startsWith("/api/")) return true;
  if (/^data:/i.test(trimmed) || /^blob:/i.test(trimmed)) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(trimmed);
}

export function pickReceivableSlipImageUrl(
  item: ReceivableListItem,
  ownerUserId?: string | null,
): string | null {
  const slipImage = item.fields.find((field) => field.key === "slip_image")?.value?.trim();
  if (slipImage && slipImage !== "—") return slipImage;

  for (const key of ["image_url", "slip_url", "image", "file_url"] as const) {
    const value = item.fields.find((field) => field.key === key)?.value?.trim();
    if (value && value !== "—" && isResolvableSlipImageUrl(value)) return value;
  }

  const imageIdField = item.fields.find((field) => field.key === "image_id")?.value;
  const fromImageId = buildReceivableSlipImageUrl(imageIdField, ownerUserId);
  if (fromImageId) return fromImageId;

  return null;
}

function appendReceivableSlipImageField(
  record: Record<string, unknown>,
  fields: ReceivableField[],
): ReceivableField[] {
  const imageId = unwrapAeraValue(record.image_id);
  const ownerUserId =
    pickNonEmptyString(record.owner_user_id) ??
    pickNonEmptyString(record.user_id);
  const imageUrl = buildReceivableSlipImageUrl(imageId, ownerUserId);
  if (!imageUrl) return fields;
  if (fields.some((field) => field.key === "slip_image" && field.value === imageUrl)) {
    return fields;
  }
  return [
    ...fields,
    { key: "slip_image", label: "ภาพสลิป", value: imageUrl },
  ];
}

export function resolveOwnerUserId(
  workspace?: Workspace | null,
): string | null {
  const fromTag = workspace?.tags?.find(
    (tag) => tag.key?.trim() === "owner_user_id",
  )?.value;
  if (typeof fromTag === "string" && fromTag.trim()) {
    return fromTag.trim();
  }

  const ownerId = workspace?.ownerId?.trim();
  if (ownerId) return ownerId;

  const storeId = workspace?.storeId?.trim();
  if (storeId) return storeId;

  return getUserIdFromAuthToken() ?? null;
}

function pickNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

const RECEIVABLE_TITLE_KEYS = [
  "slip_sender",
  "slip_sender_name",
  "sender_display_name",
  "name",
  "customer_name",
  "customer_ref",
  "customer",
  "buyer_name",
  "buyer",
  "debtor_name",
  "debtor",
  "sender_name",
  "sender_name_th",
  "sender",
  "recipient_name",
  "recipient_name_th",
  "recipient",
  "member_name",
  "display_name",
  "title",
  "label",
  "text",
  "line",
  "announcement",
  "announcement_text",
  "summary",
  "content",
  "body",
  "message_text",
  "message",
  "raw_text",
  "raw",
  "line_text",
  "value",
  "description",
  "from_name",
  "to_name",
  "account_name",
  "sender_account_name",
  "ลูกค้า",
  "ชื่อ",
  "ชื่อลูกค้า",
  "ผู้ส่ง",
  "ผู้รับ",
  "ผู้โอน",
  "รายการ",
  "ชื่อผู้ส่ง",
] as const;

const RECEIVABLE_DETAIL_KEYS = [
  "bank",
  "bank_name",
  "sender_bank",
  "ธนาคาร",
  "note",
  "remark",
  "status",
  "phone",
  "tracking_no",
  "address",
  "address_text",
  "ที่อยู่",
  "note_text",
  "memo",
  "reference",
  "order_ref",
  "chat_room",
  "channel",
] as const;

const RECEIVABLE_ADDRESS_DETAIL_KEYS = [
  "address",
  "address_text",
  "addr_text",
  "full_address",
  "ที่อยู่",
] as const;

const RECEIVABLE_ADDR_PHONE_KEYS = [
  "addr_phone",
  "phone",
  "recipient_phone",
  "contact_phone",
] as const;

const RECEIVABLE_AMOUNT_KEYS = [
  "slip_amount",
  "amount",
  "debt_amount",
  "debt",
  "balance",
  "total",
  "outstanding_amount",
  "outstanding",
  "price",
  "total_amount",
  "transfer_amount",
  "money",
  "ยอด",
  "จำนวนเงิน",
  "ยอดค้าง",
  "เงิน",
  "ยอดหนี้",
] as const;

const RECEIVABLE_CREATED_AT_KEYS = [
  "created_at",
  "recorded_at",
  "record_date",
] as const;

const RECEIVABLE_TRANSFER_DATE_KEYS = [
  "transfer_date",
  "transfer_datetime",
  "slip_date",
  "payment_date",
] as const;

const RECEIVABLE_FIELD_LABELS: Record<string, string> = {
  customer_ref: "ลูกค้า/รายการ",
  customer_name: "ลูกค้า/รายการ",
  slip_sender: "ผู้โอน",
  slip_sender_name: "ผู้โอน",
  sender_name: "ผู้โอน",
  bank: "ธนาคาร",
  bank_name: "ธนาคาร",
  sender_bank: "ธนาคาร",
  slip_amount: "รอจัดสรร",
  amount: "รอจัดสรร",
  amount_original: "ยอดเต็ม",
  amount_allocated: "จับคู่แล้ว",
  amount_paid: "รับแล้ว",
  amount_remaining: "รอจัดสรร",
  transfer_amount: "ยอดโอน",
  slip_ref: "อ้างอิง",
  reference_no: "อ้างอิง",
  reference: "อ้างอิง",
  transfer_date: "วันเวลาโอน",
  transfer_datetime: "วันเวลาโอน",
  created_at: "วันเวลาแจ้ง",
  expires_at: "ครบกำหนด",
  age_label: "อายุ",
  source_channel: "ที่มา",
  slip_image: "สลิป",
  image_url: "สลิป",
  recorded_at: "วันที่บันทึก",
  record_date: "วันที่",
  slip_date: "วันที่สลิป",
  payment_date: "วันที่ชำระ",
  updated_at: "แก้ไขเมื่อ",
  addr_name: "ลูกค้า/ผู้รับ",
  address_text: "ที่อยู่",
  address: "ที่อยู่",
  addr_phone: "โทร",
  phone: "โทร",
  announcement: "ประกาศ",
  announcement_text: "ข้อความประกาศ",
  note: "หมายเหตุ",
  remark: "หมายเหตุ",
  recipient_name: "ผู้รับ",
  queue_left_at: "ถูกลบเมื่อ",
  queue_left_reason: "สาเหตุ",
};

/** Field order within rows — mirrors final tab layouts in acceptance plan. */
const RECEIVABLE_FIELD_ORDER = [
  "transfer_date",
  "transfer_datetime",
  "slip_sender",
  "slip_sender_name",
  "sender_name",
  "customer_ref",
  "customer_name",
  "addr_name",
  "addr_phone",
  "phone",
  "address_text",
  "address",
  "source_channel",
  "age_label",
  "amount_original",
  "amount_allocated",
  "amount_paid",
  "slip_amount",
  "amount",
  "amount_remaining",
  "transfer_amount",
  "total_amount",
  "expires_at",
  "created_at",
  "recorded_at",
  "record_date",
  "slip_date",
  "payment_date",
  "slip_image",
  "bank",
  "bank_name",
  "sender_bank",
  "slip_ref",
  "reference_no",
  "announcement",
  "announcement_text",
  "note",
  "remark",
  "updated_at",
  "queue_left_at",
  "queue_left_reason",
] as const;

/** Table column order — same as RECEIVABLE_FIELD_ORDER with สถานะ near money columns. */
const RECEIVABLE_TABLE_COLUMN_ORDER = [
  "transfer_date",
  "transfer_datetime",
  "slip_sender",
  "slip_sender_name",
  "sender_name",
  "customer_ref",
  "customer_name",
  "addr_name",
  "addr_phone",
  "phone",
  "address_text",
  "address",
  "source_channel",
  "age_label",
  "amount_original",
  "amount_allocated",
  "amount_paid",
  "slip_amount",
  "amount",
  "amount_remaining",
  "expires_at",
  "__status__",
] as const;

/** Plan §1.3–1.5 — exact table columns per pending tab (สถานะ last). */
const RECEIVABLE_TABLE_COLUMNS_BY_KIND: Record<ReceivableKind, readonly string[]> = {
  address: [
    "addr_name",
    "addr_phone",
    "address_text",
    "source_channel",
    "age_label",
    "__status__",
  ],
  slip: [
    "created_at",
    "slip_sender",
    "amount_original",
    "amount_allocated",
    "slip_amount",
    "__status__",
  ],
  announcement: [
    "customer_ref",
    "amount_original",
    "amount_paid",
    "amount",
    "__status__",
  ],
};

const RECEIVABLE_AMOUNT_FIELD_KEYS = new Set([
  "slip_amount",
  "amount",
  "amount_original",
  "amount_allocated",
  "amount_paid",
  "amount_remaining",
  "debt_amount",
  "debt",
  "balance",
  "total",
  "outstanding_amount",
  "outstanding",
  "price",
  "total_amount",
  "transfer_amount",
  "money",
  "ยอด",
  "จำนวนเงิน",
  "ยอดค้าง",
  "เงิน",
  "ยอดหนี้",
]);

const RECEIVABLE_DATETIME_FIELD_KEYS = new Set([
  "created_at",
  "updated_at",
  "recorded_at",
  "record_date",
  "transfer_date",
  "transfer_datetime",
  "slip_date",
  "payment_date",
  "timestamp",
  "queue_left_at",
]);

/** Server timestamps are usually UTC even when the suffix is omitted. */
const RECEIVABLE_UTC_DATETIME_FIELD_KEYS = new Set([
  "created_at",
  "updated_at",
  "recorded_at",
  "record_date",
  "timestamp",
  "queue_left_at",
]);

/** Slip / payment timestamps are usually Bangkok local without a timezone suffix. */
const RECEIVABLE_LOCAL_DATETIME_FIELD_KEYS = new Set([
  "transfer_date",
  "transfer_datetime",
  "slip_date",
  "payment_date",
]);

const RECEIVABLE_ID_KEYS = [
  "id",
  "order_id",
  "record_id",
  "slip_id",
  "announcement_id",
] as const;

const RECEIVABLE_SKIP_SCALAR_KEYS = new Set([
  "id",
  "order_id",
  "record_id",
  "slip_id",
  "announcement_id",
  "user_id",
  "owner_user_id",
  "workspace_id",
  "image_id",
  "created_at",
  "updated_at",
  "transfer_date",
  "transfer_datetime",
  "addr_phone",
  "timestamp",
  "type",
  "kind",
  "image_url",
  "image",
  "file_url",
  "url",
  "status_code",
]);

function unwrapAeraValue(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value) && "value" in value) {
    const inner = (value as { value: unknown }).value;
    if (inner !== undefined && inner !== null) return inner;
  }
  return value;
}

function collectDisplayScalars(
  record: Record<string, unknown>,
  depth = 0,
): { strings: string[]; numbers: number[] } {
  const strings: string[] = [];
  const numbers: number[] = [];
  if (depth > 4) return { strings, numbers };

  for (const [key, raw] of Object.entries(record)) {
    if (RECEIVABLE_SKIP_SCALAR_KEYS.has(key) || key.endsWith("_id")) continue;

    const value = unwrapAeraValue(raw);
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed && trimmed.length <= 300) strings.push(trimmed);
      continue;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      numbers.push(value);
      continue;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        if (typeof entry === "string" && entry.trim()) {
          strings.push(entry.trim());
        } else if (typeof entry === "number" && Number.isFinite(entry)) {
          numbers.push(entry);
        } else if (entry && typeof entry === "object") {
          const nested = collectDisplayScalars(entry as Record<string, unknown>, depth + 1);
          strings.push(...nested.strings);
          numbers.push(...nested.numbers);
        }
      }
      continue;
    }

    if (value && typeof value === "object") {
      const nested = collectDisplayScalars(value as Record<string, unknown>, depth + 1);
      strings.push(...nested.strings);
      numbers.push(...nested.numbers);
    }
  }

  return { strings, numbers };
}

function isTechnicalFieldKey(key: string): boolean {
  const normalized = key.trim().toLowerCase();
  if (!normalized) return true;
  if (RECEIVABLE_SKIP_SCALAR_KEYS.has(normalized)) return true;
  if ((RECEIVABLE_ID_KEYS as readonly string[]).includes(normalized)) return true;
  if (/^[a-z][a-z0-9]*(_[a-z0-9]+)+$/i.test(key)) return true;
  return false;
}

function looksLikeOpaqueId(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length >= 16 && /^[a-zA-Z0-9_-]+$/.test(trimmed);
}

function looksLikeDebtorAmountMap(record: Record<string, unknown>): boolean {
  const entries = Object.entries(record).filter(
    ([, entryValue]) => unwrapAeraValue(entryValue) != null,
  );
  if (entries.length === 0) return false;
  if (entries.some(([key]) => isTechnicalFieldKey(key))) return false;

  return entries.every(([, entryValue]) => {
    const unwrapped = unwrapAeraValue(entryValue);
    return (
      typeof unwrapped === "number" ||
      (typeof unwrapped === "string" && Number.isFinite(Number(unwrapped.replace(/,/g, ""))))
    );
  });
}

function normalizeReceivableEntries(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => normalizeReceivableEntries(entry));
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of ["items", "data", "entries", "list", "rows", "results"]) {
      if (Array.isArray(record[key])) {
        return normalizeReceivableEntries(record[key]);
      }
    }

    if (looksLikeDebtorAmountMap(record)) {
      return Object.entries(record).map(([debtorName, entryValue]) => {
        const unwrapped = unwrapAeraValue(entryValue);
        if (typeof unwrapped === "number") {
          return { customer_name: debtorName, amount: unwrapped };
        }
        if (typeof unwrapped === "string") {
          return {
            customer_name: debtorName,
            line: `${debtorName} ${unwrapped}`.trim(),
          };
        }
        return { customer_name: debtorName };
      });
    }

    return [value];
  }

  if (typeof value === "string" || typeof value === "number") {
    return [value];
  }

  return [];
}

function getReceivableItemDedupKey(item: unknown): string | null {
  if (typeof item === "string") {
    const trimmed = item.trim();
    return trimmed ? `str:${trimmed.toLowerCase()}` : null;
  }

  if (typeof item === "number" && Number.isFinite(item)) {
    return `num:${item}`;
  }

  if (!item || typeof item !== "object" || Array.isArray(item)) return null;

  const record = item as Record<string, unknown>;
  const name = pickReceivableField(record, RECEIVABLE_TITLE_KEYS);
  if (name) return `name:${name.toLowerCase()}`;

  const { strings } = collectDisplayScalars(record);
  if (strings[0]) return `str:${strings[0].toLowerCase()}`;

  const id = pickReceivableField(record, RECEIVABLE_ID_KEYS);
  if (id) return `id:${id}`;

  return null;
}

function parseReceivableTextLine(text: string): {
  title: string;
  amountLabel: string | null;
} {
  const trimmed = text.trim();
  if (!trimmed) {
    return { title: "รายการค้าง", amountLabel: null };
  }

  const match = trimmed.match(/^(.+?)\s+(-?[\d,]+(?:\.\d{1,2})?)\s*$/u);
  if (match) {
    const amount = Number(match[2].replace(/,/g, ""));
    return {
      title: match[1].trim(),
      amountLabel: Number.isFinite(amount)
        ? `฿${amount.toLocaleString("th-TH", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : null,
    };
  }

  return { title: trimmed, amountLabel: null };
}

function formatReceivableAmount(value: unknown): string | null {
  const unwrapped = unwrapAeraValue(value);

  if (typeof unwrapped === "number" && Number.isFinite(unwrapped)) {
    return `฿${unwrapped.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  if (typeof unwrapped === "string") {
    const trimmed = unwrapped.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed.replace(/,/g, ""));
    if (Number.isFinite(numeric)) {
      return `฿${numeric.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  }

  return null;
}

function pickReceivableField(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = unwrapAeraValue(record[key]);
    const text = pickNonEmptyString(value);
    if (text) return text;
  }
  return null;
}

function pickReceivableDatetime(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = unwrapAeraValue(record[key]);
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      const date = new Date(value > 1e12 ? value : value * 1000);
      if (!Number.isNaN(date.getTime())) return date.toISOString();
    }
  }

  return null;
}

export function getReceivableExpiresAtMs(
  item: ReceivableListItem,
): number | null {
  const value =
    item.fields.find((field) => field.key === "expires_at")?.value ?? "";
  if (!value || value === "—") return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** Soonest expiry first; items without expires_at go last. */
export function sortReceivableItemsByExpiry(
  items: ReceivableListItem[],
): ReceivableListItem[] {
  return [...items].sort((left, right) => {
    const leftMs = getReceivableExpiresAtMs(left);
    const rightMs = getReceivableExpiresAtMs(right);
    if (leftMs === null && rightMs === null) return 0;
    if (leftMs === null) return 1;
    if (rightMs === null) return -1;
    return leftMs - rightMs;
  });
}

/** Format ISO expires_at as remaining time (e.g. "6 วัน 5 ชม. 30 นาที"). */
export function formatReceivableExpiryRemaining(
  expiresAtIso: string,
  options?: { compact?: boolean },
): string {
  const expiresMs = Date.parse(expiresAtIso);
  if (!Number.isFinite(expiresMs)) return "—";

  const remainingMs = expiresMs - Date.now();
  if (remainingMs <= 0) return "หมดอายุ";

  const totalMinutes = Math.ceil(remainingMs / 60_000);
  if (totalMinutes < 60) return `${totalMinutes} นาที`;

  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;

  // Table: largest unit only (e.g. "13 วัน", "22 ชม.").
  if (options?.compact) {
    if (days > 0) return `${days} วัน`;
    if (hours > 0) return `${hours} ชม.`;
    return `${minutes} นาที`;
  }

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} วัน`);
  if (hours > 0) parts.push(`${hours} ชม.`);
  if (minutes > 0) parts.push(`${minutes} นาที`);

  return parts.join(" ");
}

function humanizeReceivableFieldKey(
  key: string,
  kind?: ReceivableKind,
): string {
  if (kind === "announcement" && key === "amount_original") {
    return "ยอดเต็ม";
  }
  if (kind === "slip" && key === "amount_original") {
    return "ยอดโอน";
  }
  if (kind === "slip" && key === "expires_at") {
    return "หมดอายุใน";
  }
  if (kind === "address" && key === "expires_at") {
    return "หมดอายุใน";
  }
  const label = RECEIVABLE_FIELD_LABELS[key];
  if (label) return label;
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Internal / duplicate keys — never show in receivable tables. */
const RECEIVABLE_TABLE_HIDDEN_KEYS = new Set([
  "id",
  "receivable_id",
  "receivableId",
  "payment_id",
  "paymentId",
  "address_id",
  "addressId",
  "slip_id",
  "image_id",
  "owner_user_id",
  "user_id",
  "workspace_id",
  "order_id",
  "revision",
  "receivable_revision",
  "payment_revision",
  "receivable_status",
  "expires_in_seconds",
  "queue_left_order_id",
  "batch_id",
]);

function receivableTableExcludeKeys(kind?: ReceivableKind): Set<string> {
  const excluded = new Set(RECEIVABLE_TABLE_HIDDEN_KEYS);
  if (kind === "announcement") {
    // Plan columns: ลูกค้า | ยอดเต็ม | รับแล้ว | รอจัดสรร | สถานะ
    excluded.add("amount_allocated");
    excluded.add("amount_remaining");
    excluded.add("slip_amount");
    excluded.add("transfer_amount");
    excluded.add("expires_at");
  }
  if (kind === "slip") {
    // Plan columns: วันที่ | ผู้โอน | ยอดโอน | จับคู่แล้ว | รอจัดสรร | สถานะ
    excluded.add("amount_paid");
    excluded.add("amount_remaining");
    excluded.add("amount");
  }
  if (kind === "address") {
    excluded.add("amount");
    excluded.add("amount_original");
    excluded.add("amount_paid");
    excluded.add("amount_allocated");
    excluded.add("slip_amount");
  }
  return excluded;
}

/** Prefer first key in each group when several aliases exist on a row. */
const RECEIVABLE_TABLE_ALIAS_GROUPS: readonly (readonly string[])[] = [
  ["transfer_date", "transfer_datetime"],
  ["slip_sender", "slip_sender_name", "sender_name"],
  ["customer_ref", "customer_name"],
  ["addr_phone", "phone"],
  ["address_text", "address"],
];

function isReceivableAliasAlreadyCovered(
  key: string,
  seen: Set<string>,
): boolean {
  for (const group of RECEIVABLE_TABLE_ALIAS_GROUPS) {
    const index = group.indexOf(key);
    if (index <= 0) continue;
    if (group.slice(0, index).some((preferred) => seen.has(preferred))) {
      return true;
    }
  }
  return false;
}

function isReceivableTableColumnKey(
  key: string,
  kind?: ReceivableKind,
): boolean {
  return !receivableTableExcludeKeys(kind).has(key);
}

function receivableFieldSortIndex(key: string): number {
  const index = RECEIVABLE_FIELD_ORDER.indexOf(
    key as (typeof RECEIVABLE_FIELD_ORDER)[number],
  );
  return index === -1 ? RECEIVABLE_FIELD_ORDER.length + key.charCodeAt(0) : index;
}

function sortReceivableFields(fields: ReceivableField[]): ReceivableField[] {
  return [...fields].sort(
    (left, right) =>
      receivableFieldSortIndex(left.key) - receivableFieldSortIndex(right.key),
  );
}

function parseReceivableDatetimeField(
  key: string,
  value: unknown,
): Date | null {
  const unwrapped = unwrapAeraValue(value);

  if (typeof unwrapped === "number" && Number.isFinite(unwrapped)) {
    const ms = unwrapped > 1e12 ? unwrapped : unwrapped * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof unwrapped !== "string") {
    return parseDatetimeInput(String(unwrapped ?? ""));
  }

  const trimmed = unwrapped.trim();
  if (!trimmed) return null;

  if (/^\d{10,13}$/.test(trimmed)) {
    const numeric = Number(trimmed);
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed);
  const isIsoDatetime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);

  if (!hasTimezone) {
    if (RECEIVABLE_UTC_DATETIME_FIELD_KEYS.has(key) && isIsoDatetime) {
      const normalized = trimmed.replace(" ", "T").replace(/\.\d+$/, "");
      const date = new Date(`${normalized}Z`);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (RECEIVABLE_LOCAL_DATETIME_FIELD_KEYS.has(key) && isIsoDatetime) {
      const normalized = trimmed.replace(" ", "T").replace(/\.\d+$/, "");
      const date = new Date(`${normalized}+07:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (isDateOnly) {
      const date = new Date(`${trimmed}T00:00:00+07:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  return parseDatetimeInput(trimmed);
}

function formatReceivableDatetimeField(
  key: string,
  value: unknown,
): string | null {
  const date = parseReceivableDatetimeField(key, value);
  if (!date) return null;
  return formatThaiDatetimeFromDate(date);
}

function formatReceivableQueueLeftReason(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "dismissed" || trimmed === "ลบรายการ") return "ลบรายการ";
  if (trimmed === "expired" || trimmed === "หมดอายุ") return "หมดอายุ";
  return trimmed;
}

function formatReceivableFieldValue(key: string, value: unknown): string | null {
  const unwrapped = unwrapAeraValue(value);

  if (unwrapped == null) return null;

  if (typeof unwrapped === "boolean") {
    return unwrapped ? "ใช่" : "ไม่";
  }

  if (typeof unwrapped === "number" && Number.isFinite(unwrapped)) {
    if (RECEIVABLE_AMOUNT_FIELD_KEYS.has(key)) {
      return formatReceivableAmount(unwrapped);
    }
    if (RECEIVABLE_DATETIME_FIELD_KEYS.has(key)) {
      const formatted = formatReceivableDatetimeField(key, unwrapped);
      return formatted ?? String(unwrapped);
    }
    return String(unwrapped);
  }

  if (typeof unwrapped === "string") {
    const trimmed = unwrapped.trim();
    if (!trimmed) return null;

    if (key === "expires_at") {
      return Number.isFinite(Date.parse(trimmed)) ? trimmed : null;
    }

    if (key === "queue_left_reason") {
      return formatReceivableQueueLeftReason(trimmed);
    }

    if (RECEIVABLE_DATETIME_FIELD_KEYS.has(key)) {
      const formatted = formatReceivableDatetimeField(key, trimmed);
      return formatted ?? trimmed;
    }

    if (RECEIVABLE_AMOUNT_FIELD_KEYS.has(key)) {
      return formatReceivableAmount(trimmed) ?? trimmed;
    }

    return trimmed;
  }

  return null;
}

function shouldHideReceivableFieldKey(key: string): boolean {
  // Keep revisions in fields for race guards / money ops; still hidden from table columns.
  if (
    key === "revision" ||
    key === "receivable_revision" ||
    key === "payment_revision"
  ) {
    return false;
  }
  return RECEIVABLE_TABLE_HIDDEN_KEYS.has(key);
}

/** ยอดสลิป (เต็ม) ต้องแยกจาก slip_amount (คงเหลือ) — เติมเมื่อ API เก่ายังไม่ส่ง amount_original */
function ensureSlipOriginalAmount(record: Record<string, unknown>): void {
  const original = unwrapAeraValue(record.amount_original);
  const remaining = unwrapAeraValue(record.slip_amount ?? record.amount);
  const allocated = unwrapAeraValue(record.amount_allocated);
  const hasAllocated =
    typeof allocated === "number"
      ? allocated >= 0 && String(allocated) !== ""
      : typeof allocated === "string" && allocated.trim() !== "";

  if (
    (original == null || String(original).trim() === "") &&
    remaining != null &&
    String(remaining).trim() !== "" &&
    !hasAllocated
  ) {
    record.amount_original = remaining;
  }

  const origNum = Number(
    String(unwrapAeraValue(record.amount_original) ?? "").replace(/,/g, ""),
  );
  const remNum = Number(
    String(unwrapAeraValue(record.slip_amount ?? record.amount) ?? "").replace(
      /,/g,
      "",
    ),
  );
  if (
    Number.isFinite(origNum) &&
    Number.isFinite(remNum) &&
    (!hasAllocated || Number(String(allocated).replace(/,/g, "")) === 0)
  ) {
    const derived = Math.max(0, origNum - remNum);
    record.amount_allocated = derived;
  }
}

function ensureAnnouncementMoneyFields(record: Record<string, unknown>): void {
  const full = unwrapAeraValue(record.amount_original);
  const remaining = unwrapAeraValue(record.amount ?? record.amount_remaining);
  const paid = unwrapAeraValue(record.amount_paid);

  if (
    (full == null || String(full).trim() === "") &&
    remaining != null &&
    String(remaining).trim() !== "" &&
    (paid == null || String(paid).trim() === "" || Number(paid) === 0)
  ) {
    record.amount_original = remaining;
  }

  const fullNum = Number(
    String(unwrapAeraValue(record.amount_original) ?? "").replace(/,/g, ""),
  );
  const remNum = Number(
    String(unwrapAeraValue(record.amount ?? record.amount_remaining) ?? "").replace(
      /,/g,
      "",
    ),
  );
  if (
    Number.isFinite(fullNum) &&
    Number.isFinite(remNum) &&
    (paid == null || String(paid).trim() === "")
  ) {
    record.amount_paid = Math.max(0, fullNum - remNum);
  }
}

function formatAgeLabel(createdAt: string | null | undefined): string | null {
  if (!createdAt?.trim()) return null;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  const diffMs = Date.now() - created.getTime();
  if (diffMs < 0) return "เพิ่งเข้า";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${Math.max(1, mins)} นาที`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours} ชม.`;
  const days = Math.floor(hours / 24);
  return `${days} วัน`;
}

function ensureAddressDisplayFields(
  record: Record<string, unknown>,
  createdAt: string | null,
): void {
  if (!unwrapAeraValue(record.source_channel)) {
    record.source_channel = "LINE";
  }
  if (!unwrapAeraValue(record.age_label)) {
    const age = formatAgeLabel(createdAt);
    if (age) record.age_label = age;
  }
}

function extractReceivableDisplayFields(
  record: Record<string, unknown>,
): ReceivableField[] {
  const fields: ReceivableField[] = [];

  for (const [key, raw] of Object.entries(record)) {
    if (shouldHideReceivableFieldKey(key)) continue;

    const value = formatReceivableFieldValue(key, raw);
    if (!value) continue;

    fields.push({
      key,
      label: humanizeReceivableFieldKey(key),
      value,
    });
  }

  return sortReceivableFields(fields);
}

function mergeReceivableFields(
  left: ReceivableField[],
  right: ReceivableField[],
): ReceivableField[] {
  const merged = new Map<string, ReceivableField>();
  for (const field of left) merged.set(field.key, field);
  for (const field of right) {
    if (field.value) merged.set(field.key, field);
  }
  return sortReceivableFields([...merged.values()]);
}

export function buildReceivableTableColumns(
  items: ReceivableListItem[],
  kind?: ReceivableKind,
): ReceivableTableColumn[] {
  const availableKeys = new Set<string>();

  for (const item of items) {
    for (const field of item.fields) {
      if (isReceivableTableColumnKey(field.key, kind)) {
        availableKeys.add(field.key);
      }
    }
  }

  if (kind == null) {
    const columns: ReceivableTableColumn[] = [];
    const seen = new Set<string>();
    for (const key of RECEIVABLE_TABLE_COLUMN_ORDER) {
      if (key === "__status__") {
        if (!seen.has(key)) {
          columns.push({ key, label: "สถานะ" });
          seen.add(key);
        }
        continue;
      }
      if (!availableKeys.has(key) || seen.has(key)) continue;
      if (isReceivableAliasAlreadyCovered(key, seen)) continue;
      columns.push({
        key,
        label: humanizeReceivableFieldKey(key),
      });
      seen.add(key);
    }
    if (!seen.has("__status__")) {
      columns.push({ key: "__status__", label: "สถานะ" });
    }
    return columns;
  }

  // Fixed plan headers — always show, resolve alias to a key that has data when possible.
  const columns: ReceivableTableColumn[] = [];
  for (const preferred of RECEIVABLE_TABLE_COLUMNS_BY_KIND[kind]) {
    if (preferred === "__status__") {
      columns.push({ key: "__status__", label: "สถานะ" });
      continue;
    }
    const group =
      RECEIVABLE_TABLE_ALIAS_GROUPS.find((aliases) =>
        aliases.includes(preferred),
      ) ?? ([preferred] as const);
    const resolved =
      group.find((key) => availableKeys.has(key)) ?? preferred;
    columns.push({
      key: resolved,
      label: humanizeReceivableFieldKey(preferred, kind),
    });
  }
  return columns;
}

/** Resolve a table cell value, falling back across known field aliases. */
export function resolveReceivableTableFieldValue(
  item: ReceivableListItem,
  key: string,
): string {
  const direct = item.fields.find((field) => field.key === key)?.value;
  if (direct) {
    if (RECEIVABLE_DATETIME_FIELD_KEYS.has(key)) {
      return formatReceivableFieldValue(key, direct) ?? direct;
    }
    return direct;
  }

  const group = RECEIVABLE_TABLE_ALIAS_GROUPS.find((aliases) =>
    aliases.includes(key),
  );
  if (group) {
    for (const alt of group) {
      if (alt === key) continue;
      const value = item.fields.find((field) => field.key === alt)?.value;
      if (value) {
        if (RECEIVABLE_DATETIME_FIELD_KEYS.has(alt) || RECEIVABLE_DATETIME_FIELD_KEYS.has(key)) {
          return formatReceivableFieldValue(alt, value) ?? value;
        }
        return value;
      }
    }
  }
  if (key === "created_at" && item.createdAt) {
    return (
      formatReceivableFieldValue("created_at", item.createdAt) ?? item.createdAt
    );
  }
  return "—";
}

/** Filter + relabel receivable fields for table/drawer display (hides internal keys). */
export function filterReceivableFieldsForDisplay(
  fields: ReceivableField[],
  kind?: ReceivableKind,
): ReceivableField[] {
  return fields
    .filter((field) => isReceivableTableColumnKey(field.key, kind))
    .map((field) => {
      const formatted = formatReceivableFieldValue(field.key, field.value);
      return {
        ...field,
        label: humanizeReceivableFieldKey(field.key, kind),
        value: formatted ?? field.value,
      };
    });
}

function pickReceivableAmount(record: Record<string, unknown>): string | null {
  for (const key of RECEIVABLE_AMOUNT_KEYS) {
    const formatted = formatReceivableAmount(unwrapAeraValue(record[key]));
    if (formatted) return formatted;
  }

  const { numbers } = collectDisplayScalars(record);
  const money = numbers.find((value) => value > 0) ?? numbers[0];
  return money != null ? formatReceivableAmount(money) : null;
}

function buildReceivableDetailLine(
  ...parts: Array<string | null | undefined>
): string | null {
  const unique = new Set<string>();
  for (const part of parts) {
    const trimmed = part?.trim();
    if (!trimmed || unique.has(trimmed)) continue;
    unique.add(trimmed);
  }
  const line = [...unique].join(" · ");
  return line || null;
}

function sanitizeReceivableTitle(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || looksLikeOpaqueId(trimmed) || isTechnicalFieldKey(trimmed)) {
    return null;
  }
  return trimmed;
}

const RECEIVABLE_STATUS_ORDER: ReceivableBucket[] = [
  "pending",
  "review",
  "dismissed",
  "expired",
];

function buildReceivableMergeKey(
  item: unknown,
  title: string,
  amountLabel: string | null,
): string {
  if (typeof item === "string") {
    const trimmed = item.trim();
    return getReceivableItemDedupKey(trimmed) ?? `str:${trimmed.toLowerCase()}`;
  }

  if (typeof item === "number" && Number.isFinite(item)) {
    return `num:${item}`;
  }

  if (item && typeof item === "object" && !Array.isArray(item)) {
    const record = item as Record<string, unknown>;
    const id = pickReceivableField(record, RECEIVABLE_ID_KEYS);
    if (id) return `id:${id}`;
  }

  const normalizedTitle = title.trim().toLowerCase();
  const normalizedAmount =
    amountLabel?.replace(/[^\d.,]/g, "").replace(/,/g, "") ?? "";
  if (normalizedTitle) {
    return `party:${normalizedTitle}|${normalizedAmount}`;
  }

  return "";
}

function mergeReceivableStatuses(
  left: ReceivableBucket[],
  right: ReceivableBucket[],
): ReceivableBucket[] {
  const set = new Set([...left, ...right]);
  return RECEIVABLE_STATUS_ORDER.filter((status) => set.has(status));
}

function mergeReceivableKindLabels(
  left: string | null,
  right: string | null,
): string | null {
  if (!left) return right;
  if (!right) return left;
  if (left === right) return left;
  if (left.includes("สลิป") && right.includes("สลิป")) return "สลิป";
  return right;
}

function combineReceivableListItems(
  existing: ReceivableListItem,
  incoming: ReceivableListItem,
): ReceivableListItem {
  return {
    key: existing.key,
    mergeKey: existing.mergeKey || incoming.mergeKey,
    statuses: mergeReceivableStatuses(existing.statuses, incoming.statuses),
    title:
      incoming.title && incoming.title !== "รายการค้าง"
        ? incoming.title
        : existing.title,
    detail: incoming.detail || existing.detail,
    amountLabel: incoming.amountLabel ?? existing.amountLabel,
    kindLabel: mergeReceivableKindLabels(existing.kindLabel, incoming.kindLabel),
    createdAt: incoming.createdAt ?? existing.createdAt,
    transferDate: incoming.transferDate ?? existing.transferDate,
    addrPhone: incoming.addrPhone ?? existing.addrPhone,
    fields: mergeReceivableFields(existing.fields, incoming.fields),
    receivableId: incoming.receivableId ?? existing.receivableId ?? null,
    paymentId: incoming.paymentId ?? existing.paymentId ?? null,
    addressId: incoming.addressId ?? existing.addressId ?? null,
    workspaceId: incoming.workspaceId ?? existing.workspaceId ?? null,
  };
}

function pickReceivableSubjectIds(source: unknown): {
  receivableId: string | null;
  paymentId: string | null;
  addressId: string | null;
} {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return { receivableId: null, paymentId: null, addressId: null };
  }

  const record = source as Record<string, unknown>;
  return {
    receivableId: pickReceivableField(record, [
      "receivable_id",
      "receivableId",
      "receivable_order_id",
      "receivableOrderId",
    ]),
    paymentId: pickReceivableField(record, [
      "payment_id",
      "paymentId",
      "payment_order_id",
      "paymentOrderId",
    ]),
    addressId: pickReceivableField(record, [
      "address_id",
      "addressId",
      "address_order_id",
      "addressOrderId",
    ]),
  };
}

function pickWorkspaceId(source: unknown): string | null {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }
  const record = source as Record<string, unknown>;
  const raw = record.workspace_id ?? record.workspaceId;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return null;
}

function finalizeReceivableListItem(
  item: Omit<
    ReceivableListItem,
    "mergeKey" | "statuses" | "createdAt" | "transferDate" | "addrPhone" | "fields"
  > & {
    mergeKey?: string;
    statuses?: ReceivableBucket[];
    createdAt?: string | null;
    transferDate?: string | null;
    addrPhone?: string | null;
    fields?: ReceivableField[];
  },
  source: unknown,
  bucket: ReceivableBucket,
): ReceivableListItem {
  const subjectIds = pickReceivableSubjectIds(source);
  return {
    ...item,
    mergeKey:
      item.mergeKey?.trim() ||
      buildReceivableMergeKey(source, item.title, item.amountLabel) ||
      item.key,
    statuses: item.statuses ?? [bucket],
    createdAt: item.createdAt ?? null,
    transferDate: item.transferDate ?? null,
    addrPhone: item.addrPhone ?? null,
    fields: item.fields ?? [],
    receivableId: item.receivableId ?? subjectIds.receivableId,
    paymentId: item.paymentId ?? subjectIds.paymentId,
    addressId: item.addressId ?? subjectIds.addressId,
    workspaceId: item.workspaceId ?? pickWorkspaceId(source),
  };
}

export function mergeReceivableListItems(
  pending: ReceivableListItem[],
  review: ReceivableListItem[],
): ReceivableListItem[] {
  const merged = new Map<string, ReceivableListItem>();

  for (const item of [...pending, ...review]) {
    const mergeKey = item.mergeKey || item.key;
    const existing = merged.get(mergeKey);

    if (!existing) {
      merged.set(mergeKey, {
        ...item,
        statuses: [...item.statuses],
      });
      continue;
    }

    merged.set(mergeKey, combineReceivableListItems(existing, item));
  }

  return [...merged.values()];
}

function toReceivableListItem(
  item: unknown,
  bucket: ReceivableBucket,
  index: number,
  sourceKind?: string,
): ReceivableListItem | null {
  const kindLabel = sourceKind?.trim() || null;

  if (typeof item === "number" && Number.isFinite(item)) {
    return finalizeReceivableListItem(
      {
        key: getReceivableItemDedupKey(item) ?? `${bucket}:num:${index}`,
        title: kindLabel ?? "รายการค้าง",
        detail: null,
        amountLabel: formatReceivableAmount(item),
        kindLabel,
        fields: [],
      },
      item,
      bucket,
    );
  }

  if (typeof item === "string") {
    const trimmed = item.trim();
    if (!trimmed) return null;
    const { title, amountLabel } = parseReceivableTextLine(trimmed);
    return finalizeReceivableListItem(
      {
        key: getReceivableItemDedupKey(trimmed) ?? `${bucket}:str:${index}`,
        title,
        detail: kindLabel,
        amountLabel,
        kindLabel,
        fields: [],
      },
      trimmed,
      bucket,
    );
  }

  if (Array.isArray(item)) {
    if (item.length === 0) return null;

    const stringParts = item
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (stringParts.length === item.length) {
      return toReceivableListItem(stringParts.join(" "), bucket, index, sourceKind);
    }

    const [first, second] = item;
    if (typeof first === "string" && first.trim()) {
      return toReceivableListItem(
        { name: first.trim(), amount: second },
        bucket,
        index,
        sourceKind,
      );
    }

    return null;
  }

  if (!item || typeof item !== "object") return null;

  const record = item as Record<string, unknown>;
  const key =
    getReceivableItemDedupKey(item) ??
    `${bucket}:${sourceKind ?? "item"}:${index}`;

  const isAddressItem = Boolean(kindLabel?.includes("ที่อยู่"));

  let title = sanitizeReceivableTitle(
    pickReceivableField(record, RECEIVABLE_TITLE_KEYS),
  );
  const bank = pickReceivableField(record, [
    "bank",
    "bank_name",
    "sender_bank",
    "ธนาคาร",
  ]);
  const detail = isAddressItem
    ? pickReceivableField(record, RECEIVABLE_ADDRESS_DETAIL_KEYS)
    : pickReceivableField(record, RECEIVABLE_DETAIL_KEYS);
  let amountLabel = isAddressItem ? null : pickReceivableAmount(record);
  const addrPhone = isAddressItem
    ? pickReceivableField(record, RECEIVABLE_ADDR_PHONE_KEYS)
    : null;

  const { strings } = collectDisplayScalars(record);
  const displayStrings = strings.filter(
    (value) => !looksLikeOpaqueId(value) && !isTechnicalFieldKey(value),
  );

  if (!title && displayStrings.length > 0) {
    const parsed = parseReceivableTextLine(displayStrings[0]);
    title = sanitizeReceivableTitle(parsed.title);
    if (!isAddressItem && !amountLabel && parsed.amountLabel) {
      amountLabel = parsed.amountLabel;
    }
  }

  if (!isAddressItem && !amountLabel) {
    for (const text of displayStrings) {
      const parsed = parseReceivableTextLine(text);
      if (parsed.amountLabel) {
        amountLabel = parsed.amountLabel;
        if (!title) {
          title = sanitizeReceivableTitle(parsed.title);
        }
        break;
      }
    }
  }

  const finalTitle = title ?? kindLabel ?? "รายการค้าง";
  const finalDetail = buildReceivableDetailLine(bank, detail);
  const createdAt = pickReceivableDatetime(record, RECEIVABLE_CREATED_AT_KEYS);
  const transferDate = pickReceivableDatetime(
    record,
    RECEIVABLE_TRANSFER_DATE_KEYS,
  );
  if (kindLabel?.includes("สลิป")) {
    ensureSlipOriginalAmount(record);
  }
  if (kindLabel?.includes("ประกาศ")) {
    ensureAnnouncementMoneyFields(record);
  }
  if (kindLabel?.includes("ที่อยู่")) {
    ensureAddressDisplayFields(record, createdAt);
  }
  const fields = appendReceivableSlipImageField(
    record,
    extractReceivableDisplayFields(record),
  );

  return finalizeReceivableListItem(
    {
      key,
      title: finalTitle,
      detail: finalDetail,
      amountLabel,
      kindLabel,
      createdAt,
      transferDate,
      addrPhone,
      fields,
    },
    item,
    bucket,
  );
}

function firstReceivableArray(
  ...values: unknown[]
): unknown[] | undefined {
  for (const value of values) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length > 0) return entries.map(([name, entryValue]) => ({ name, ...(typeof entryValue === "object" && entryValue ? entryValue as object : { value: entryValue }) }));
    }
  }
  return undefined;
}

function normalizePendingResponse(
  response: OrdersPendingResponse,
): OrdersPendingResponse {
  const record = response as Record<string, unknown>;
  return {
    ...response,
    announcements: firstReceivableArray(
      response.announcements,
      record.pending_announcements,
      record.debts,
      record.debtors,
      record.lines,
      record.items,
    ),
    slips: firstReceivableArray(
      response.slips,
      record.pending_slips,
      record.unmatched_slips,
    ),
  };
}

function normalizeReviewResponse(
  response: OrdersReviewResponse,
): OrdersReviewResponse {
  const record = response as Record<string, unknown>;
  return {
    ...response,
    announcements: firstReceivableArray(
      response.announcements,
      record.review_announcements,
      record.lines,
      record.items,
    ),
    addresses: firstReceivableArray(
      response.addresses,
      record.review_addresses,
      record.pending_addresses,
    ),
    slips: firstReceivableArray(
      response.slips,
      record.review_slips,
      record.unmatched_slips,
    ),
  };
}

function collectReceivableListItems(
  entries: { sourceKind: string; items: unknown }[],
  bucket: ReceivableBucket,
): ReceivableListItem[] {
  const out: ReceivableListItem[] = [];
  let index = 0;

  for (const { sourceKind, items } of entries) {
    for (const item of normalizeReceivableEntries(items)) {
      const listItem = toReceivableListItem(item, bucket, index, sourceKind);
      index += 1;
      if (listItem) out.push(listItem);
    }
  }

  return dedupeReceivableListItems(out);
}

function dedupeReceivableListItems(
  items: ReceivableListItem[],
): ReceivableListItem[] {
  const merged = new Map<string, ReceivableListItem>();

  for (const item of items) {
    const dedupeKey = item.mergeKey || item.key;
    const existing = merged.get(dedupeKey);

    if (!existing) {
      merged.set(dedupeKey, {
        ...item,
        statuses: [...item.statuses],
      });
      continue;
    }

    merged.set(dedupeKey, combineReceivableListItems(existing, item));
  }

  return [...merged.values()];
}

export function buildReceivableListItems(
  pending: OrdersPendingResponse | null,
  review: OrdersReviewResponse | null,
): {
  items: ReceivableListItem[];
  pending: ReceivableListItem[];
  review: ReceivableListItem[];
  byKind: Record<ReceivableKind, ReceivableListItem[]>;
} {
  const pendingAnnouncements = collectReceivableListItems(
    [{ sourceKind: "ประกาศค้าง", items: pending?.announcements ?? [] }],
    "pending",
  );
  const pendingSlips = collectReceivableListItems(
    [{ sourceKind: "สลิปรอปิดยอด", items: pending?.slips ?? [] }],
    "pending",
  );

  const reviewAnnouncements = collectReceivableListItems(
    [{ sourceKind: "ประกาศรอตรวจ", items: review?.announcements ?? [] }],
    "review",
  );
  const reviewAddresses = collectReceivableListItems(
    [{ sourceKind: "ที่อยู่รอตรวจ", items: review?.addresses ?? [] }],
    "review",
  );
  const reviewSlips = collectReceivableListItems(
    [{ sourceKind: "สลิปรอตรวจ", items: review?.slips ?? [] }],
    "review",
  );

  const announcements = mergeReceivableListItems(
    pendingAnnouncements,
    reviewAnnouncements,
  );
  const slips = mergeReceivableListItems(pendingSlips, reviewSlips);
  const addresses = dedupeReceivableListItems(reviewAddresses);

  const pendingItems = [...pendingAnnouncements, ...pendingSlips];
  const reviewItems = [
    ...reviewAnnouncements,
    ...reviewAddresses,
    ...reviewSlips,
  ];

  return {
    items: [...announcements, ...addresses, ...slips],
    pending: pendingItems,
    review: reviewItems,
    byKind: {
      announcement: announcements,
      address: addresses,
      slip: slips,
    },
  };
}

function formatInactiveBaht(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return `฿${value.toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Map DB inactive (dismissed/expired) rows into the same list shape as hot-queue tabs. */
export function inactiveOrderToReceivableListItem(
  item: InactiveOrderItem,
): ReceivableListItem | null {
  const kind =
    item.kind === "slip" || item.kind === "announcement" || item.kind === "address"
      ? item.kind
      : null;
  if (!kind) return null;

  const bucket: ReceivableBucket =
    item.queue_left_reason === "expired" ? "expired" : "dismissed";
  const kindLabel =
    kind === "slip" ? "สลิป" : kind === "address" ? "ที่อยู่" : "ประกาศ";
  const orderId = (item.order_id || item.source_ref || item.entity_id || "").trim();
  if (!orderId && !item.entity_id) return null;

  const mergeKey = `inactive:${kind}:${item.entity_id || orderId}`;
  const remainingLabel = formatInactiveBaht(
    item.amount_remaining ?? (kind === "address" ? null : item.amount),
  );
  const title = (item.title || "").trim() || kindLabel;
  const leftAt = item.queue_left_at || null;

  const fields: ReceivableField[] = [
    { key: "customer_ref", label: "ลูกค้า/รายการ", value: title },
    { key: "addr_name", label: "ชื่อ", value: title },
    { key: "recipient_name", label: "ผู้รับ", value: title },
    { key: "slip_sender", label: "ผู้โอน", value: title },
  ];

  if (item.amount != null) {
    const amt = formatInactiveBaht(item.amount) || "—";
    fields.push(
      { key: "amount_original", label: "ยอดเต็ม", value: amt },
      { key: "slip_amount", label: "ยอดโอน", value: amt },
      { key: "amount", label: "ยอด", value: remainingLabel || amt },
    );
  }
  if (item.amount_paid != null) {
    fields.push({
      key: "amount_paid",
      label: "รับแล้ว",
      value: formatInactiveBaht(item.amount_paid) || "—",
    });
  }
  if (item.amount_allocated != null) {
    fields.push({
      key: "amount_allocated",
      label: "จัดสรรแล้ว",
      value: formatInactiveBaht(item.amount_allocated) || "—",
    });
  }
  if (item.amount_remaining != null) {
    fields.push({
      key: "amount_remaining",
      label: "รอจัดสรร",
      value: formatInactiveBaht(item.amount_remaining) || "—",
    });
  }
  if (item.transfer_date) {
    fields.push({
      key: "transfer_date",
      label: "วันโอน",
      value:
        formatReceivableDatetimeField("transfer_date", item.transfer_date) ||
        item.transfer_date,
    });
  }
  if (item.addr_phone) {
    fields.push({ key: "addr_phone", label: "โทร", value: item.addr_phone });
    fields.push({ key: "phone", label: "โทร", value: item.addr_phone });
  }
  if (item.address_text) {
    fields.push({
      key: "address_text",
      label: "ที่อยู่",
      value: item.address_text,
    });
  }
  if (leftAt) {
    // Keep ISO so date filters / sort can parse; table cells format for display.
    fields.push({
      key: "queue_left_at",
      label: "ถูกลบเมื่อ",
      value: leftAt,
    });
    if (bucket === "expired") {
      // Lets status/date logic treat departure time as the expiry moment.
      fields.push({
        key: "expires_at",
        label: "ครบกำหนด",
        value: leftAt,
      });
    }
  }
  fields.push({
    key: "queue_left_reason",
    label: "สาเหตุ",
    value: bucket === "expired" ? "หมดอายุ" : "ลบรายการ",
  });

  return {
    key: mergeKey,
    mergeKey,
    statuses: [bucket],
    title,
    detail:
      bucket === "expired"
        ? "หมดอายุจากคิวค้าง"
        : "ลบออกจากคิวค้าง",
    amountLabel: remainingLabel,
    kindLabel,
    createdAt: item.created_at || leftAt,
    transferDate: item.transfer_date || null,
    addrPhone: item.addr_phone || null,
    fields,
    receivableId: kind === "announcement" ? item.entity_id : null,
    paymentId: kind === "slip" ? item.entity_id : null,
    addressId: kind === "address" ? item.entity_id : null,
    workspaceId: item.workspace_id?.trim() || null,
  };
}

export function mergeInactiveIntoReceivableLists(
  lists: ReturnType<typeof buildReceivableListItems>,
  inactiveItems: InactiveOrderItem[],
): ReturnType<typeof buildReceivableListItems> {
  const activeKeys = new Set(lists.items.map((item) => item.mergeKey));
  const activeOrderIds = new Set(
    lists.items
      .map((item) => getReceivableOrderId(item))
      .filter((id): id is string => Boolean(id)),
  );
  const activeEntityIds = new Set(
    lists.items.flatMap((item) =>
      [item.receivableId, item.paymentId, item.addressId]
        .map((id) => id?.trim())
        .filter((id): id is string => Boolean(id)),
    ),
  );

  const byKind: Record<ReceivableKind, ReceivableListItem[]> = {
    announcement: [...lists.byKind.announcement],
    address: [...lists.byKind.address],
    slip: [...lists.byKind.slip],
  };

  for (const raw of inactiveItems) {
    const item = inactiveOrderToReceivableListItem(raw);
    if (!item) continue;
    if (activeKeys.has(item.mergeKey)) continue;
    const orderId = (raw.order_id || raw.source_ref || "").trim();
    if (orderId && activeOrderIds.has(orderId)) continue;
    if (raw.entity_id && activeEntityIds.has(String(raw.entity_id))) continue;

    const kind =
      raw.kind === "slip" || raw.kind === "announcement" || raw.kind === "address"
        ? raw.kind
        : null;
    if (!kind) continue;
    byKind[kind].push(item);
    activeKeys.add(item.mergeKey);
  }

  return {
    ...lists,
    items: [...byKind.announcement, ...byKind.address, ...byKind.slip],
    byKind,
  };
}

export function summarizeReceivableItemsByKind(
  items: ReceivableListItem[],
  kind: ReceivableKind,
): ReceivableDebtsSummary {
  return summarizeReceivableItems(filterReceivableItemsByKind(items, kind));
}

export function formatReceivableBreakdownLabel(
  pendingCount: number,
  reviewCount: number,
): string {
  if (pendingCount <= 0 && reviewCount <= 0) {
    return "ไม่มีรายการค้าง";
  }

  const parts: string[] = [];
  if (pendingCount > 0) {
    parts.push(`ค้าง ${pendingCount.toLocaleString("th-TH")}`);
  }
  if (reviewCount > 0) {
    parts.push(`รอตรวจ ${reviewCount.toLocaleString("th-TH")}`);
  }

  return `${parts.join(" ")} รายการ`;
}

export function summarizeReceivableItems(
  items: ReceivableListItem[],
): ReceivableDebtsSummary {
  const pendingCount = items.filter((item) =>
    item.statuses.includes("pending"),
  ).length;
  const reviewCount = items.filter((item) =>
    item.statuses.includes("review"),
  ).length;
  const hotCount = items.filter(
    (item) =>
      item.statuses.includes("pending") || item.statuses.includes("review"),
  ).length;
  const totalCount = items.length;
  const breakdownLabel = formatReceivableBreakdownLabel(
    pendingCount,
    reviewCount,
  );

  if (hotCount <= 0 && totalCount <= 0) {
    return {
      pendingCount: 0,
      reviewCount: 0,
      totalCount: 0,
      cardSubtitle: "ไม่มีรายการค้าง",
      detailNowLabel: "ไม่มีรายการค้างชำระ",
    };
  }

  if (hotCount <= 0 && totalCount > 0) {
    const inactiveCount = totalCount;
    const label = `รายการเก่า ${inactiveCount.toLocaleString("th-TH")} รายการ`;
    return {
      pendingCount: 0,
      reviewCount: 0,
      totalCount,
      cardSubtitle: label,
      detailNowLabel: label,
    };
  }

  return {
    pendingCount,
    reviewCount,
    totalCount,
    cardSubtitle: breakdownLabel,
    detailNowLabel: breakdownLabel,
  };
}

export function summarizeReceivableDebts(
  pending: OrdersPendingResponse | null,
  review: OrdersReviewResponse | null,
): ReceivableDebtsSummary {
  const lists = buildReceivableListItems(pending, review);
  return summarizeReceivableItems(lists.items);
}

async function fetchOrdersEndpoint<T>(
  path: string,
): Promise<T> {
  const res = await marketApiFetch(path, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Fetch failed: ${res.status}${text ? ` — ${text.slice(0, 180)}` : ""}`,
    );
  }
  return (await res.json()) as T;
}

type ReceivableDebtsDetails = {
  pending: OrdersPendingResponse;
  review: OrdersReviewResponse;
  summary: ReceivableDebtsSummary;
  lists: ReturnType<typeof buildReceivableListItems>;
  hotCounts: {
    pending: HotQueueKindCountTotals;
    review: HotQueueKindCountTotals;
  };
};
const receivableDebtsInflight = new Map<string, Promise<ReceivableDebtsDetails>>();

function emptyHotQueueCounts(): HotQueueKindCountTotals {
  return { announcements: 0, slips: 0, addresses: 0 };
}

function normalizeHotQueueCounts(
  counts?: HotQueueKindCounts | null,
): HotQueueKindCountTotals {
  return {
    announcements: counts?.announcements ?? 0,
    slips: counts?.slips ?? 0,
    addresses: counts?.addresses ?? 0,
  };
}

function isArchivedReceivableStatus(status?: string | null): boolean {
  const value = status?.trim() ?? "";
  return value === "หมดอายุ" || value === "รายการที่ลบ";
}

function archivedInactiveReason(
  status?: string | null,
): InactiveOrderReason | undefined {
  const value = status?.trim() ?? "";
  if (value === "หมดอายุ") return "expired";
  if (value === "รายการที่ลบ") return "dismissed";
  return undefined;
}

function receivableListQueryKey(query?: ReceivableListQuery): string {
  const archived = isArchivedReceivableStatus(query?.status);
  // ยอดค้าง/สลิปค้าง share one pending hydrate (both kinds) so match pickers work.
  const kindKey =
    !archived && (query?.kind === "announcement" || query?.kind === "slip")
      ? "pending-money"
      : (query?.kind ?? "");
  return [
    kindKey,
    query?.dateFrom?.trim() ?? "",
    query?.dateTo?.trim() ?? "",
    query?.q?.trim() ?? "",
    query?.status?.trim() ?? "",
  ].join("|");
}

function appendHotQueueParams(
  params: URLSearchParams,
  query?: ReceivableListQuery,
) {
  if (query?.kind) params.set("kind", query.kind);
  if (query?.dateFrom?.trim()) params.set("date_from", query.dateFrom.trim());
  if (query?.dateTo?.trim()) params.set("date_to", query.dateTo.trim());
  if (query?.q?.trim()) params.set("q", query.q.trim());
}

/**
 * Pending fetch for ยอดค้าง / สลิปค้าง must include both kinds.
 * `?kind=slip` drops announcements, so ตัดยอดค้าง on a slip shows "ไม่มียอดค้างให้ตัด".
 */
export function pendingQueryForReceivableMoneyMatch(
  query?: ReceivableListQuery,
): ReceivableListQuery | undefined {
  if (!query) return undefined;
  if (query.kind === "announcement" || query.kind === "slip") {
    return {
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      q: query.q,
      status: query.status,
    };
  }
  return query;
}

async function buildReceivableDebtsDetails(
  ownerUserId: string,
  workspaceId: string,
  query?: ReceivableListQuery,
): Promise<ReceivableDebtsDetails> {
  const ws = workspaceId.trim();
  if (!ws) {
    throw new Error("workspace_id is required");
  }
  const archived = isArchivedReceivableStatus(query?.status);
  const kind = query?.kind;
  const hotQuery = archived ? undefined : query;
  const pendingQuery = archived
    ? undefined
    : pendingQueryForReceivableMoneyMatch(query);
  // Tab-scoped fetches: ยอดค้าง/สลิปค้าง only need pending; ที่อยู่ค้าง only needs review.
  // Skip the other hydrate so FilterBar date/q does not pay for unused kinds twice.
  // Do not kind-scope pending: match modal needs the counterpart kind.
  const wantPending = !archived && kind !== "address";
  const wantReview = !archived && kind !== "announcement" && kind !== "slip";
  const [pendingRaw, reviewRaw, inactiveRaw] = await Promise.all([
    wantPending
      ? fetchOrdersPending(ownerUserId, ws, pendingQuery)
      : Promise.resolve({
          announcements: [],
          slips: [],
          counts: emptyHotQueueCounts(),
        }),
    wantReview
      ? fetchOrdersReview(ownerUserId, ws, hotQuery)
      : Promise.resolve({
          announcements: [],
          addresses: [],
          slips: [],
          counts: emptyHotQueueCounts(),
        }),
    archived
      ? fetchOrdersInactive(ownerUserId, {
          limit: 200,
          workspaceId: ws,
          kind: query?.kind,
          reason: archivedInactiveReason(query?.status),
          dateFrom: query?.dateFrom,
          dateTo: query?.dateTo,
          q: query?.q,
        }).catch(async (err) => {
          console.warn("[receivable] inactive fetch failed, retrying once", err);
          try {
            return await fetchOrdersInactive(ownerUserId, {
              limit: 200,
              workspaceId: ws,
              kind: query?.kind,
              reason: archivedInactiveReason(query?.status),
              dateFrom: query?.dateFrom,
              dateTo: query?.dateTo,
              q: query?.q,
            });
          } catch (retryErr) {
            console.warn("[receivable] inactive fetch retry failed", retryErr);
            return { items: [] as InactiveOrderItem[] };
          }
        })
      : Promise.resolve({ items: [] as InactiveOrderItem[] }),
  ]);
  let pending = normalizePendingResponse(pendingRaw);
  let review = normalizeReviewResponse(reviewRaw);
  if (!archived && (kind === "announcement" || kind === "slip")) {
    // Copy both kinds so ตัดยอดค้าง / จับคู่การชำระเงิน see counterparts
    // with the same รอตรวจ badge as the ยอดค้าง / สลิปค้าง tabs.
    review = {
      ...review,
      announcements: pending.announcements,
      slips: pending.slips,
      counts: {
        announcements: pending.counts?.announcements ?? 0,
        slips: pending.counts?.slips ?? 0,
        addresses: 0,
      },
    };
  }
  const hotLists = buildReceivableListItems(pending, review);
  const merged = mergeInactiveIntoReceivableLists(
    hotLists,
    inactiveRaw.items ?? [],
  );
  // Defense in depth: even if upstream omits workspace_id filtering, never mix shops.
  const lists = filterReceivableListsByWorkspace(merged, ws);

  return {
    pending,
    review,
    summary: summarizeReceivableItems(lists.items),
    lists,
    hotCounts: {
      pending: normalizeHotQueueCounts(pending.counts),
      review: normalizeHotQueueCounts(review.counts),
    },
  };
}

/**
 * GET /api/v1/orders/pending/{owner}?workspace_id=...
 *
 * Always pass the current workspace. Omitting `workspace_id` no longer returns
 * every pending order for the owner — only the internal `_none` bucket.
 */
export async function fetchOrdersPending(
  ownerUserId: string,
  workspaceId: string,
  query?: ReceivableListQuery,
): Promise<OrdersPendingResponse> {
  const id = ownerUserId.trim();
  if (!id) {
    throw new Error("owner_user_id is required");
  }
  const ws = workspaceId.trim();
  if (!ws) {
    throw new Error("workspace_id is required");
  }
  const params = new URLSearchParams({ workspace_id: ws });
  appendHotQueueParams(params, query);
  return fetchOrdersEndpoint<OrdersPendingResponse>(
    `/api/v1/orders/pending/${encodeURIComponent(id)}?${params.toString()}`,
  );
}

/**
 * GET /api/v1/orders/review/{owner}?workspace_id=...
 *
 * Always pass the current workspace. Omitting `workspace_id` no longer returns
 * every pending order for the owner — only the internal `_none` bucket.
 */
export async function fetchOrdersReview(
  ownerUserId: string,
  workspaceId: string,
  query?: ReceivableListQuery,
): Promise<OrdersReviewResponse> {
  const id = ownerUserId.trim();
  if (!id) {
    throw new Error("owner_user_id is required");
  }
  const ws = workspaceId.trim();
  if (!ws) {
    throw new Error("workspace_id is required");
  }
  const params = new URLSearchParams({ workspace_id: ws });
  appendHotQueueParams(params, query);
  return fetchOrdersEndpoint<OrdersReviewResponse>(
    `/api/v1/orders/review/${encodeURIComponent(id)}?${params.toString()}`,
  );
}

/** Soft-dismissed + TTL-expired items from DB (รายการเก่า). Not hot-queue Redis. */
export async function fetchOrdersInactive(
  ownerUserId: string,
  options?: {
    reason?: InactiveOrderReason | "all";
    kind?: "slip" | "announcement" | "address" | "all" | string;
    limit?: number;
    workspaceId?: string;
    dateFrom?: string | null;
    dateTo?: string | null;
    q?: string | null;
  },
): Promise<OrdersInactiveResponse> {
  const id = ownerUserId.trim();
  if (!id) {
    throw new Error("owner_user_id is required");
  }
  const params = new URLSearchParams();
  if (options?.reason && options.reason !== "all") {
    params.set("reason", options.reason);
  }
  if (options?.kind && options.kind !== "all") {
    params.set("kind", options.kind);
  }
  if (options?.limit != null) {
    params.set("limit", String(options.limit));
  }
  if (options?.workspaceId?.trim()) {
    params.set("workspace_id", options.workspaceId.trim());
  }
  if (options?.dateFrom?.trim()) {
    params.set("date_from", options.dateFrom.trim());
  }
  if (options?.dateTo?.trim()) {
    params.set("date_to", options.dateTo.trim());
  }
  if (options?.q?.trim()) {
    params.set("q", options.q.trim());
  }
  const qs = params.toString();
  return fetchOrdersEndpoint<OrdersInactiveResponse>(
    `/api/v1/orders/inactive/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchReceivableDebtsSummary(
  ownerUserId: string,
  workspaceId: string,
): Promise<ReceivableDebtsSummary> {
  const details = await fetchReceivableDebtsDetails(ownerUserId, {
    workspaceId,
  });
  return details.summary;
}

export async function fetchReceivableDebtsDetails(
  ownerUserId: string,
  options: { force?: boolean; workspaceId: string; query?: ReceivableListQuery },
): Promise<ReceivableDebtsDetails> {
  const id = ownerUserId.trim();
  if (!id) {
    throw new Error("owner_user_id is required");
  }
  const workspaceId = options.workspaceId.trim();
  if (!workspaceId) {
    throw new Error("workspace_id is required");
  }
  const cacheKey = `${id}::${workspaceId}::${receivableListQueryKey(options.query)}`;

  // After match/undo, drop any in-flight pre-change fetch so we don't reuse stale results.
  if (options.force) {
    receivableDebtsInflight.delete(cacheKey);
  } else {
    const inflight = receivableDebtsInflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const promise = buildReceivableDebtsDetails(id, workspaceId, options.query).finally(
    () => {
      // Only clear if this promise is still the registered one (a forced refresh may have replaced it).
      if (receivableDebtsInflight.get(cacheKey) === promise) {
        receivableDebtsInflight.delete(cacheKey);
      }
    },
  );
  receivableDebtsInflight.set(cacheKey, promise);
  return promise;
}

export type PaymentAllocationInput = {
  payment_order_id: string;
  amount?: string;
};

export type ReceivableAllocationInput = {
  receivable_order_id: string;
  amount?: string;
};

export type MatchReceivablePaymentResult = {
  status?: string;
  confirm_id?: string;
  payment_order_ids?: string[];
  receivable_order_ids?: string[];
  allocations_applied?: PaymentAllocationInput[] | ReceivableAllocationInput[];
  receivable_status?: string | null;
  amount_remaining?: number | null;
  operation_id?: string | null;
  operation_key?: string | null;
  after?: Record<string, unknown> | null;
  error?: string;
  detail?: string;
};

export function getReceivableOrderId(item: ReceivableListItem): string | null {
  for (const candidate of [item.mergeKey, item.key]) {
    const trimmed = candidate?.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("id:")) return trimmed.slice(3).trim();
    if (!trimmed.includes(":")) return trimmed;
  }
  return null;
}

export async function matchReceivablePayment(
  ownerUserId: string,
  receivableOrderId: string,
  allocations: PaymentAllocationInput[] | string | string[],
): Promise<MatchReceivablePaymentResult> {
  const id = ownerUserId.trim();
  if (!id) {
    throw new Error("owner_user_id is required");
  }

  const resolvedAllocations: PaymentAllocationInput[] = Array.isArray(allocations)
    && allocations.length > 0
    && typeof allocations[0] === "object"
    && allocations[0] !== null
    && "payment_order_id" in allocations[0]
    ? (allocations as PaymentAllocationInput[])
        .map((item) => ({
          payment_order_id: item.payment_order_id.trim(),
          amount: item.amount?.trim() || undefined,
        }))
        .filter((item) => item.payment_order_id)
    : (Array.isArray(allocations) ? allocations : [allocations])
        .map((item) => String(item).trim())
        .filter(Boolean)
        .map((paymentOrderId) => ({ payment_order_id: paymentOrderId }));

  if (resolvedAllocations.length === 0) {
    throw new Error("at least one payment_order_id is required");
  }

  const res = await marketApiFetch(
    `/api/v1/orders/match/${encodeURIComponent(id)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        receivable_order_id: receivableOrderId,
        allocations: resolvedAllocations,
      }),
    },
  );

  const data = (await res.json().catch(() => ({}))) as MatchReceivablePaymentResult & {
    details?: unknown;
  };

  if (!res.ok) {
    const detail =
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `Match failed (${res.status})`;
    throw new Error(detail);
  }

  return data;
}

/** สลิปหนึ่งใบ → ตัดหลายยอดค้าง ใน operation เดียว (Undo คืนครบ) */
export async function matchPaymentToReceivables(
  ownerUserId: string,
  paymentOrderId: string,
  allocations: ReceivableAllocationInput[],
): Promise<MatchReceivablePaymentResult> {
  const id = ownerUserId.trim();
  if (!id) {
    throw new Error("owner_user_id is required");
  }
  const payOid = paymentOrderId.trim();
  if (!payOid) {
    throw new Error("payment_order_id is required");
  }
  const resolved = allocations
    .map((item) => ({
      receivable_order_id: item.receivable_order_id.trim(),
      amount: item.amount?.trim() || undefined,
    }))
    .filter((item) => item.receivable_order_id);
  if (resolved.length === 0) {
    throw new Error("at least one receivable_order_id is required");
  }

  const res = await marketApiFetch(
    `/api/v1/orders/match/${encodeURIComponent(id)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        payment_order_id: payOid,
        receivable_allocations: resolved,
      }),
    },
  );

  const data = (await res.json().catch(() => ({}))) as MatchReceivablePaymentResult & {
    details?: unknown;
  };

  if (!res.ok) {
    const detail =
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `Match failed (${res.status})`;
    throw new Error(detail);
  }

  return data;
}
